import { Fragment, useCallback, useState } from 'react';
import { MenuItem } from '@mantine/core';
import clsx from 'clsx';

import { ChipGroup } from '../chipGroup/ChipGroup';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import {
  scrollByWithLayoutOffset,
  useScrollTopOffset,
} from '../layout/LayoutContext';
import { Menu } from '../menu/Menu';
import { Tooltip } from '../tooltip/Tooltip';

import { IconButton } from './IconActions';
import {
  WidgetAction,
  WidgetChipGroupProps,
  WidgetClassNames,
  WidgetMoreAction,
  WidgetProps,
} from './Widget';

import styles from './widget.module.scss';

type ActionState = { loading: boolean };

export type WidgetHeaderProps = {
  actions?: WidgetProps['actions'];
  classNames?: WidgetClassNames;
  collapsible?: WidgetProps['collapsible'];
  dataTestId?: string;
  enableScrollElementToTop?: boolean;
  opened?: boolean;
  toggleContent: () => void;
  title: WidgetProps['title'];
  titleDataTestId?: string;
  chipGroup?: WidgetChipGroupProps;
};

export function WidgetHeader({
  actions,
  classNames,
  collapsible = false,
  dataTestId,
  enableScrollElementToTop = false,
  opened = true,
  toggleContent,
  title,
  titleDataTestId,
  chipGroup,
}: WidgetHeaderProps) {
  const offset = useScrollTopOffset();

  const [actionStates, setActionStates] = useState<ActionState[]>(
    actions?.map((_) => ({ loading: false })) ?? []
  );

  const triggerAction = useCallback(
    (action: WidgetAction | WidgetMoreAction, idx?: number) => {
      const updateActionItemAt = (idx: number, data: Partial<ActionState>) => {
        const updatedActionStates = [...actionStates];
        updatedActionStates.splice(idx, 1, {
          ...actionStates[idx],
          ...data,
        });
        setActionStates(updatedActionStates);
      };

      // use loading indicator for async action callbacks only
      return action.cb?.constructor.name === 'AsyncFunction'
        ? async () => {
            idx != null && updateActionItemAt(idx, { loading: true });
            await action.cb?.();
            idx != null && updateActionItemAt(idx, { loading: false });
          }
        : action.cb;
    },
    [actionStates]
  );

  const hasChipGroupData = (chipGroup?.data?.length ?? 0) > 0;
  const hasContents =
    actions?.length || collapsible || title || hasChipGroupData;

  if (!hasContents) {
    return null;
  }

  return (
    <div
      className={clsx(styles['header'], classNames?.header)}
      data-testid={dataTestId}
    >
      {collapsible && (
        <button
          aria-label="Collapse widget"
          className={clsx(
            styles['header__collapsible-icon'],
            opened && styles['header__collapsible-icon--opened']
          )}
          type="button"
          onClick={toggleContent}
        />
      )}
      <div
        className={clsx(styles['header__title'], classNames?.title, {
          [styles['header__title__clickable']]: enableScrollElementToTop,
        })}
        data-testid={titleDataTestId}
        onClick={(e) => {
          if (enableScrollElementToTop) {
            const header = e.currentTarget.parentElement;

            if (header) {
              scrollByWithLayoutOffset(header, offset.current ?? 0);
            }
          }
        }}
      >
        {title}
      </div>
      {chipGroup?.data != null && chipGroup.data.length > 0 && (
        <div style={{ maxWidth: '40%' }}>
          <ChipGroup
            data={chipGroup.data}
            defaultValue={chipGroup.defaultValue}
            value={chipGroup.value}
            onChange={chipGroup.onChange}
          />
        </div>
      )}
      <div className={styles['header__actions']}>
        {actions?.map((action, idx) => (
          <Fragment key={`${idx}-${action.icon.name}`}>
            {action.moreActions ? (
              <MoreMenu
                action={action}
                idx={idx}
                loading={actionStates[idx]?.loading}
                triggerAction={triggerAction}
              />
            ) : (
              <IconButton
                action={action}
                loading={actionStates[idx]?.loading}
                onClick={triggerAction(action, idx)}
              />
            )}
            {action.separatorAfter ? (
              <span
                className={styles['header__action-separator']}
                role="separator"
              />
            ) : null}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

type MoreMenuProps = {
  action: WidgetAction;
  idx: number;
  loading?: boolean;
  triggerAction: (
    action: WidgetAction | WidgetMoreAction,
    idx?: number
  ) => (() => void | Promise<void>) | undefined;
};

function MoreMenu({ action, idx, loading, triggerAction }: MoreMenuProps) {
  const [opened, setOpened] = useState(false);

  return (
    <Menu
      onChange={setOpened}
      opened={opened}
      offset={0}
      position="bottom-end"
      menuTarget={
        <IconButton
          action={action}
          loading={loading}
          onClick={() => setOpened(true)}
        />
      }
      menuDropdown={action.moreActions?.map((ma) => (
        <MenuItem
          className={styles['menu-item']}
          key={ma.label}
          disabled={ma.disabled}
          onClick={triggerAction(ma, idx)}
        >
          <span>{ma.label}</span>
          {ma.tooltip != null && (
            <Tooltip label={ma.tooltip} usePortal={true} width="200px">
              <GoogleFontIcon name="info" />
            </Tooltip>
          )}
        </MenuItem>
      ))}
    />
  );
}
